import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Tooltip,
} from "@mui/material";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import ScoreboardOutlinedIcon from "@mui/icons-material/ScoreboardOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import { useIntl } from "react-intl";
import { buildCompetitionAdminRoutes } from "constants/competition-admin-routes";
import performRequest from "network/perform-request";
import { competitionsNavigationSummaryRoute } from "network/api-routes";
import UserContext from "context/UserContext";
import ShareModal from "./ShareModal";
import { isEventDrawerItemActive } from "./adminEventUtils";

const eventNameCache = new Map();

const CompetitionDrawer = ({ competitionId, currentPathname, onNavigate, onRouteNavigate, collapsed = false }) => {
  const id = competitionId;
  const intl = useIntl();
  const routes = buildCompetitionAdminRoutes(id);
  const { user } = React.useContext(UserContext);
  const cacheKey = `${user?.id ?? "anonymous"}:${id}`;
  const [eventName, setEventName] = useState(() => eventNameCache.get(cacheKey) || null);
  const [openShareModal, setOpenShareModal] = useState(false);

  useEffect(() => {
    if (!id) {
      setEventName(null);
      return;
    }

    const cachedName = eventNameCache.get(cacheKey);
    if (cachedName) {
      setEventName(cachedName);
      return;
    }

    setEventName(null);
    let active = true;
    performRequest("POST", competitionsNavigationSummaryRoute, { competition_uuid: id }, true, user)
      .then((response) => {
        const name = response.data.data?.name;
        if (!name) return;

        eventNameCache.set(cacheKey, name);
        if (active) setEventName(name);
      })
      .catch(() => undefined);

    return () => {
      active = false;
    };
  }, [cacheKey, id, user]);

  const groups = [
    {
      label: "General",
      items: [
        { label: "Resumen", link: routes.overview, icon: <DashboardOutlinedIcon />, exact: true },
        { label: "Torneos", link: routes.tournaments, icon: <EmojiEventsOutlinedIcon />, nested: true },
        { label: "Inscripciones", link: routes.registrations, icon: <FactCheckOutlinedIcon /> },
      ],
    },
    {
      label: "Operación",
      items: [
        { label: "Programación", link: routes.scheduling, icon: <EventAvailableOutlinedIcon /> },
        { label: "Calendario de partidos", link: routes.schedule, icon: <CalendarMonthOutlinedIcon /> },
        { label: "Sedes y canchas", link: routes.clubs, icon: <LocationOnOutlinedIcon /> },
      ],
    },
    {
      label: "Configuración",
      items: [
        { label: "Detalles del evento", link: routes.edit, icon: <EditOutlinedIcon /> },
        { label: "Campos de inscripción", link: routes.customFields, icon: <ListAltOutlinedIcon /> },
        { label: "Puntos por partido", link: routes.matchScoring, icon: <ScoreboardOutlinedIcon /> },
        { label: "Administradores", link: routes.managers, icon: <PeopleAltOutlinedIcon /> },
      ],
    },
  ];

  const renderNavigationItem = (item) => {
    const active = Boolean(currentPathname) && isEventDrawerItemActive(currentPathname, item);
    const label = intl.formatMessage({ id: item.label });
    const button = (
      <ListItemButton
        component="button"
        type="button"
        selected={active}
        onClick={() => {
          onRouteNavigate?.(item.link);
          onNavigate?.();
        }}
        sx={{ justifyContent: collapsed ? "center" : "flex-start", px: collapsed ? 1.5 : 2 }}
      >
        <ListItemIcon sx={{ minWidth: collapsed ? 0 : 34, mr: collapsed ? 0 : 1, justifyContent: "center" }}>
          {item.icon}
        </ListItemIcon>
        {!collapsed && <ListItemText primary={label} />}
      </ListItemButton>
    );

    return (
      <Tooltip key={item.link} title={collapsed ? label : ""} placement="right">
        {button}
      </Tooltip>
    );
  };

  return (
    <Box>
      {!collapsed && (
        <Box sx={{ px: 1, pb: 2 }}>
          {eventName && <ListItemText primary={eventName} primaryTypographyProps={{ noWrap: true, fontWeight: 700 }} />}
        </Box>
      )}

      <Tooltip title={collapsed ? intl.formatMessage({ id: "Compartir evento" }) : ""} placement="right">
        <Button
          variant="contained"
          color="primary"
          disableElevation
          fullWidth={!collapsed}
          onClick={() => setOpenShareModal(true)}
          aria-label={intl.formatMessage({ id: "Compartir evento" })}
          sx={collapsed ? { height: 44, minWidth: 0, width: "100%" } : { mb: 1 }}
        >
          {collapsed ? <ShareOutlinedIcon /> : intl.formatMessage({ id: "Compartir evento" })}
        </Button>
      </Tooltip>

      <List disablePadding>
        {groups.map((group) => (
          <React.Fragment key={group.label}>
            {!collapsed && (
              <ListSubheader component="div" disableSticky sx={{ bgcolor: "transparent" }}>
                {intl.formatMessage({ id: group.label })}
              </ListSubheader>
            )}
            {group.items.map(renderNavigationItem)}
            <Divider sx={{ my: 1 }} />
          </React.Fragment>
        ))}
      </List>

      <Dialog open={openShareModal} onClose={() => setOpenShareModal(false)} fullWidth maxWidth="sm">
        <ShareModal competitionId={id} onClose={() => setOpenShareModal(false)} />
      </Dialog>
    </Box>
  );
};

export default CompetitionDrawer;
